import React from 'react';
import { Phone, Bot, CheckCircle, Send, RefreshCw, Loader2, MessageCircle, AlertTriangle, User, Eye, Ban } from 'lucide-react';
import { StatusBadge } from './shared';

export function ConversationsTab({ 
  leadsNeedingAttention, 
  selectedLead, 
  conversationHistory, 
  manualResponse, 
  sendingResponse,
  onSelectLead, 
  onManualResponseChange, 
  onSendResponse, 
  onBlacklist,
  onRefresh 
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Lista de Leads */}
      <div className="lg:col-span-1 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="text-yellow-400" size={20} />
            Precisam de Atenção
          </h3>
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
            title="Atualizar"
          >
            <RefreshCw size={16} />
          </button>
        </div>
        
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {leadsNeedingAttention.map(lead => (
            <button
              key={lead.id}
              onClick={() => onSelectLead(lead.id)}
              className={`w-full text-left p-4 rounded-xl transition-colors ${
                selectedLead?.lead_id === lead.id
                  ? 'bg-blue-600/30 border-blue-500'
                  : 'bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50'
              } border`}
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium truncate">
                  {lead.business_name || lead.phone}
                </span>
                <StatusBadge status={lead.status} />
              </div>
              <div className="flex items-center gap-2 mt-1 text-gray-400 text-sm">
                <Phone size={12} />
                {lead.phone}
              </div>
              {lead.last_contact_at && (
                <div className="text-gray-500 text-xs mt-1">
                  Último contato: {new Date(lead.last_contact_at).toLocaleString('pt-BR')}
                </div>
              )}
            </button>
          ))}
          
          {leadsNeedingAttention.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              <CheckCircle size={40} className="mx-auto mb-2 opacity-50" />
              <p>Nenhum lead precisa de atenção</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Área de Conversa */}
      <div className="lg:col-span-2">
        {selectedLead ? (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 flex flex-col h-[600px]">
            {/* Header do Chat */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <User size={18} />
                  {selectedLead.business_name || selectedLead.phone}
                </h3>
                <div className="flex items-center gap-4 mt-1 text-sm">
                  <span className="text-gray-400">{selectedLead.phone}</span>
                  <StatusBadge status={selectedLead.status} />
                  {selectedLead.is_bot && (
                    <span className="text-orange-400 flex items-center gap-1">
                      <Bot size={14} />
                      Bot Detectado
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => onBlacklist(selectedLead.lead_id)}
                className="flex items-center gap-1 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                title="Adicionar à blacklist"
              >
                <Ban size={16} />
                Blacklist
              </button>
            </div>
            
            {/* Mensagens */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {conversationHistory.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.from_lead ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-xl ${
                      msg.from_lead
                        ? 'bg-gray-700 text-white'
                        : msg.type === 'manual'
                          ? 'bg-purple-600 text-white'
                          : 'bg-blue-600 text-white'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    <div className={`text-xs mt-1 ${msg.from_lead ? 'text-gray-400' : 'text-blue-200'}`}>
                      {msg.timestamp && new Date(msg.timestamp).toLocaleString('pt-BR')}
                      {msg.type === 'manual' && ' (manual)'}
                      {msg.type === 'follow_up' && ' (follow-up)'}
                    </div>
                  </div>
                </div>
              ))}
              
              {conversationHistory.length === 0 && (
                <div className="text-center py-10 text-gray-500">
                  <MessageCircle size={40} className="mx-auto mb-2 opacity-50" />
                  <p>Nenhuma mensagem ainda</p>
                </div>
              )}
            </div>
            
            {/* Input de Resposta */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualResponse}
                  onChange={(e) => onManualResponseChange(e.target.value)}
                  placeholder="Digite sua resposta..."
                  className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSendResponse();
                    }
                  }}
                />
                <button
                  onClick={onSendResponse}
                  disabled={sendingResponse || !manualResponse.trim()}
                  className="px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-xl flex items-center gap-2"
                >
                  {sendingResponse ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Pressione Enter para enviar. Esta mensagem será enviada diretamente via WhatsApp.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 flex items-center justify-center h-[600px]">
            <div className="text-center text-gray-500">
              <Eye size={48} className="mx-auto mb-3 opacity-50" />
              <p>Selecione um lead para ver a conversa</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
